import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type LoginResponse } from '../api/auth'
import { fetchStats, type CrmStats } from '../api/stats'
import {
  Button, ButtonLink, Callout, Card, CardContent, CardGrid, CardGridItem, CardHeader, CircularProgress, DataTable, DataTableEmptyRow,
  EmptyState as UiEmptyState, Page, PageHeader, SegmentedButton, StatCard, StatGrid, TextField,
} from '../components/ui'
import { BranchChartCard, CommissionsChartCard, TrendChartCard } from '../components/StatsCharts'
import { chartRange, type ChartRange } from '../components/chartRange'
import { AnnualRevenueSection } from '../components/AnnualRevenueSection'
import { OperatorMonthlySection } from '../components/OperatorMonthlySection'
import './dashboard.css'

/**
 * DEPLOY INCIDENT (2026-08-30): the frontend build was pushed live ahead of a matching backend
 * deploy. CrmStats's array fields are typed as always-present (correctly, for a CURRENT backend —
 * StatsController.Get always emits them), but the stale backend actually running omitted several
 * that were added after it was last built (leadsByLostReason among them), so `undefined.length`
 * crashed the whole page. This normalizes every array field to `[]` when the wire payload omits
 * it, so the dashboard degrades to an empty section instead of a white screen against ANY
 * backend version — current or lagging — without weakening CrmStats's own type for the (normal)
 * case where frontend and backend are in sync. Safe to remove once the backend deploy in progress
 * lands and this mismatch class is no longer possible; harmless to keep after that.
 */
function normalizeStats(data: CrmStats): CrmStats {
  return {
    ...data,
    contractsByStatus: data.contractsByStatus ?? [],
    leadsByLostReason: data.leadsByLostReason ?? [],
    productsByType: data.productsByType ?? [],
    contractsByOperator: data.contractsByOperator ?? [],
    recentContracts: data.recentContracts ?? [],
    upcomingAppointments: data.upcomingAppointments ?? [],
    activeContests: data.activeContests ?? [],
    activeTargets: data.activeTargets ?? [],
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  auth: LoginResponse
  onLogout: () => void
}

type Period = 'today' | 'week' | 'month' | 'quarter' | 'year'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatEur = (v: number): string =>
  '€ ' + v.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

/**
 * TASK-0200 / REQ-058-014: renders a real monetary amount, or an em-dash when the backend OMITTED
 * it because the caller's UserCategory lacks CanSeeRealAmounts (StatsController.Get sends no key at
 * all in that case — see api/stats.ts). Deliberately NOT `formatEur(v ?? 0)`: a fabricated "€ 0" is
 * indistinguishable from a genuine zero, the same "never render a number that looks real but isn't"
 * rule PeriodComparisonPage.tsx (TASK-0194) and this dashboard's own absent Obiettivi
 * currentValue/percentage already follow. Also covers the pre-load state (stats still null), where
 * a dash is likewise more honest than a zero.
 */
const formatEurOrDash = (v: number | undefined): string => (v === undefined ? '—' : formatEur(v))

const formatNum = (v: number): string => v.toLocaleString('it-IT')

/**
 * REQ-014-002: renders `contracts.activationRateOnContracts` — a 0..1 fraction rounded to 4
 * decimals server-side, or `null` when the selected period holds no contracts at all (an UNDEFINED
 * rate, not a real 0%). Verbatim the same helper FunnelsPage.tsx already uses for the identically
 * named funnel field, so the same number renders the same way on both pages. Deliberately NOT
 * `(v ?? 0) * 100`: a fabricated "0,0%" is indistinguishable from a genuine zero rate, the exact
 * "never render a number that looks real but isn't" rule `formatEurOrDash` above already follows.
 * `undefined` covers the pre-load state (stats still null), where a dash is likewise honest.
 */
const formatPctOrDash = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—'
  return (v * 100).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
}

const formatDate = (v: string): string =>
  new Date(v).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })

const formatDateTime = (v: string): string =>
  new Date(v).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

// TASK-0070: local-calendar-date "YYYY-MM-DD" — deliberately NOT toISOString() (which converts
// to UTC first and can shift the calendar day by ±1 depending on the browser's local offset).
// This is the exact same string shape a native <input type="date"> holds, and what
// fetchStats()/StatsController.Get's dateFrom/dateTo already expect.
const toIsoDateLocal = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * TASK-0070: the 5 period buttons compute their effective [dateFrom, dateTo] range CLIENT-SIDE
 * and send it through the exact same dateFrom/dateTo query parameters as the custom Dal/Al
 * filter — rather than adding a server-side `period` enum — so both paths run through the one
 * StatsController.Get code path/UTC-safe range logic. "Period to date": dateFrom is the start of
 * the period, dateTo is always today (StatsController treats dateTo as inclusive of the WHOLE
 * day, so this covers everything created in the period so far).
 */
const periodRange = (period: Period): { from: string; to: string } => {
  const now = new Date()
  const to = toIsoDateLocal(now)
  let from: Date
  switch (period) {
    case 'today':
      from = now
      break
    case 'week': {
      const day = now.getDay() // 0=Sun..6=Sat
      const diffToMonday = day === 0 ? -6 : 1 - day
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMonday)
      break
    }
    case 'month':
      from = new Date(now.getFullYear(), now.getMonth(), 1)
      break
    case 'quarter': {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3
      from = new Date(now.getFullYear(), quarterStartMonth, 1)
      break
    }
    case 'year':
      from = new Date(now.getFullYear(), 0, 1)
      break
  }
  return { from: toIsoDateLocal(from), to }
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Oggi',
  week: 'Settimana',
  month: 'Mese',
  quarter: 'Trimestre',
  year: 'Anno',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmptyRow({ cols = 3 }: { cols?: number }) {
  return <DataTableEmptyRow colSpan={cols} icon="inbox" title="Nessun dato" />
}

function EmptyState({ text = 'Nessun dato' }: { text?: string }) {
  return <UiEmptyState icon="inbox" title={text} compact />
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function Dashboard({ auth }: Props) {
  const navigate = useNavigate()
  const [stats, setStats] = useState<CrmStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // TASK-0070: selectedPeriod is null while a custom Dal/Al range is authoritative (no period
  // button highlighted) — previously this state only ever drove which button looked "active",
  // never what was actually fetched.
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>('month')
  // I campi Dal/Al mostrano sempre l'intervallo in uso, anche quando lo decide un pulsante di periodo.
  const [dateFrom, setDateFrom] = useState<string>(() => periodRange('month').from)
  const [dateTo, setDateTo] = useState<string>(() => periodRange('month').to)

  // Intervallo dei grafici: lo stesso del periodo caricato (filtri vuoti = ultimi 12 mesi).
  const [range, setRange] = useState<ChartRange>(() => chartRange(periodRange('month').from, periodRange('month').to))

  const loadStats = (from?: string, to?: string) => {
    setRange(prev => {
      const next = chartRange(from || undefined, to || undefined)
      return prev.from === next.from && prev.to === next.to ? prev : next
    })
    setLoading(true)
    setError(null)
    fetchStats(from || undefined, to || undefined)
      .then(data => setStats(normalizeStats(data)))
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (selectedPeriod) {
      const range = periodRange(selectedPeriod)
      loadStats(range.from, range.to)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod])

  const handlePeriodChange = (period: Period) => {
    setSelectedPeriod(period)
    const r = periodRange(period)
    setDateFrom(r.from)
    setDateTo(r.to)
  }

  const handleCustomFilter = (e?: React.FormEvent) => {
    e?.preventDefault()
    // A custom range supersedes any period button — none of the 5 stays highlighted.
    setSelectedPeriod(null)
    loadStats(dateFrom, dateTo)
  }

  // ── Derived stats ──────────────────────────────────────────────────────────
  const contratti = stats?.contracts.total ?? 0
  // TASK-0200: stays `number | undefined` — no `?? 0` fallback, so the "Fatturato" KPI card below
  // renders an em-dash (not "€ 0") when the backend omitted totalValue for a caller without
  // CanSeeRealAmounts.
  const fatturato: number | undefined = stats?.contracts.totalValue
  // REQ-014-003: fatturato presunto/incassato — same `number | undefined` shape as `fatturato`
  // above, for the identical reason (StatsController.Get omits all three together for a caller
  // without CanSeeRealAmounts). `incassato` replaces the previous hardcoded `0` placeholder now
  // that the backend actually aggregates Contract.CollectedAmount — see that field's own "no
  // application write path yet" caveat in api/stats.ts before assuming it moves with real payments.
  const fatturatoPresunto: number | undefined = stats?.contracts.expectedValue
  const incassato: number | undefined = stats?.contracts.collectedValue
  const leadAttivi = stats?.leads.total ?? 0
  // REQ-014-002: spec section 14's production indicators. The three counts fall back to 0 only for
  // the pre-load state (stats still null) exactly like `contratti`/`leadAttivi` above — unlike
  // `fatturato`, the backend never omits them (they are counts, present in both branches of
  // StatsController.Get's CanSeeRealAmounts if/else), so no undefined-vs-real-zero ambiguity exists
  // for them. The rate is the opposite case and stays `number | null | undefined` — see
  // formatPctOrDash.
  const attivazioni = stats?.contracts.active ?? 0
  const annullate = stats?.contracts.cancelled ?? 0
  const rifiutate = stats?.contracts.rejected ?? 0
  const tassoAttivazione: number | null | undefined = stats?.contracts.activationRateOnContracts
  // REQ-014-003: provvigioni presunte/maturate/pagate — company-wide figures, gated by
  // CanSeeTotalCommissions (a flag distinct from CanSeeRealAmounts above — see api/stats.ts),
  // so all three stay `number | undefined` for the identical "absent, not zeroed" reason.
  const provvigioniPresunte: number | undefined = stats?.totalPresumedCommissions
  const provvigioniMaturate: number | undefined = stats?.totalCommissions
  const provvigioniPagate: number | undefined = stats?.totalPaidCommissions

  return (
    <Page>
      <PageHeader title="Dashboard" icon="dashboard" />
      {/* ── Filtri periodo ─────────────────────────────────────────────── */}
      <form className="md-dash-filter" role="search" onSubmit={e => { e.preventDefault(); handleCustomFilter() }}>
        {/* Periodo: gruppo connesso su una riga propria (scorre in orizzontale se non entra) */}
        <div className="md-dash-filter__period">
          <SegmentedButton
            aria-label="Periodo"
            value={selectedPeriod ?? ''}
            onChange={v => handlePeriodChange(v as Period)}
            options={(Object.keys(PERIOD_LABELS) as Period[]).map(p => ({ value: p, label: PERIOD_LABELS[p] }))}
          />
        </div>

        {/* Intervallo personalizzato: Dal, Al e Applica sulla stessa riga */}
        <div className="md-dash-filter__range">
          <TextField
            type="date"
            name="date_from"
            label="Dal"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
          <span className="md-dash-filter__sep" aria-hidden="true">—</span>
          <TextField
            type="date"
            name="date_to"
            label="Al"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
          <Button type="submit" variant="tonal" icon="search" disabled={loading}>Applica</Button>
          {loading && <CircularProgress size="sm" label="Caricamento" />}
        </div>
      </form>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <Callout tone="error" role="alert">
          {error}
        </Callout>
      )}

      {/* ── Row 1 – KPI stat cards ────────────────────────────────────────── */}
      <StatGrid>
        {/* Contratti */}
        <StatCard onClick={() => navigate('/contracts')} label="Contratti" value={formatNum(contratti)} icon="description" />

        {/* Fatturato presunto — REQ-014-003: contracts.expectedValue, the same
            CanSeeRealAmounts-gated shape as Fatturato/Incassato next to it. */}
        <StatCard onClick={() => navigate('/finance/receivables')} label="Fatturato presunto" value={formatEurOrDash(fatturatoPresunto)} icon="query_stats" tone="primary" />

        {/* Fatturato (maturato/accrued) */}
        <StatCard onClick={() => navigate('/finance/receivables')} label="Fatturato" value={formatEurOrDash(fatturato)} icon="euro" tone="success" />

        {/* Incassato — REQ-014-003: now contracts.collectedValue (real, migrated data), replacing
            the previous hardcoded 0 placeholder. formatEurOrDash, not formatEur: the backend omits
            this key entirely for a caller without CanSeeRealAmounts, same as Fatturato. */}
        <StatCard onClick={() => navigate('/finance/receivables')} label="Incassato" value={formatEurOrDash(incassato)} icon="payments" tone="warning" />

        {/* Lead Attivi */}
        <StatCard onClick={() => navigate('/leads')} label="Lead Attivi" value={formatNum(leadAttivi)} icon="person_search" tone="primary" />
      </StatGrid>

      {/* ── Row 1b – REQ-014-002 production indicators (Dashboard agente) ──── */}
      {/* Spec section 14: attivazioni, annullamenti, rifiuti and the conversion rate, alongside the
          pratiche/contratti count already shown in Row 1. All four come from the same period- and
          ADR-008-filtered contracts section as "Contratti" above, so they always agree with it. */}
      <StatGrid>
        {/* Attivazioni */}
        <StatCard onClick={() => navigate('/contracts?status=Attivata')} label="Attivazioni" value={formatNum(attivazioni)} icon="check_circle" tone="success" />

        {/* Annullate */}
        <StatCard onClick={() => navigate('/contracts?status=Annullata')} label="Annullate" value={formatNum(annullate)} icon="cancel" tone="warning" />

        {/* Rifiutate */}
        <StatCard onClick={() => navigate('/contracts?status=Rifiutata')} label="Rifiutate" value={formatNum(rifiutate)} icon="block" tone="error" />

        {/* Tasso di attivazione — il supporting text spiega numeratore/denominatore, stessa disambiguazione
            FunnelsPage.tsx applies to the identically-named field (KPI_DICTIONARY.md §4 / DEC-008:
            a bare "Activation Rate" would collide with a different-denominator KPI). */}
        <StatCard onClick={() => navigate('/contracts')} label="Tasso di attivazione" supporting="attivazioni su contratti" value={formatPctOrDash(tassoAttivazione)} icon="percent" tone="primary" />
      </StatGrid>

      {/* ── Row 1c – REQ-014-003 commission figures (Dashboard agente) ──────── */}
      {/* Gated by CanSeeTotalCommissions, a DIFFERENT UserCategory flag from CanSeeRealAmounts
          above — see api/stats.ts's remarks on totalCommissions/totalPresumedCommissions/
          totalPaidCommissions. All three stay `number | undefined` and render via
          formatEurOrDash, never a fabricated "€ 0", for a caller the backend denies the figures
          to entirely (the key is absent from the response, not zeroed). */}
      <StatGrid>
        {/* Provvigioni presunte */}
        <StatCard onClick={() => navigate('/commissions')} label="Provvigioni presunte" value={formatEurOrDash(provvigioniPresunte)} icon="hourglass_top" tone="primary" />

        {/* Provvigioni maturate */}
        <StatCard onClick={() => navigate('/commissions')} label="Provvigioni maturate" value={formatEurOrDash(provvigioniMaturate)} icon="paid" tone="success" />

        {/* Provvigioni pagate */}
        <StatCard onClick={() => navigate('/commissions')} label="Provvigioni pagate" value={formatEurOrDash(provvigioniPagate)} icon="account_balance_wallet" tone="warning" />
      </StatGrid>

      {/* ── Grafici: andamento e ripartizione per ramo, stesso periodo dei filtri ── */}
      <CardGrid min={360}>
        <TrendChartCard range={range} />
        <BranchChartCard range={range} />
      </CardGrid>
      <CommissionsChartCard range={range} />

      {/* ── La mia produzione (REQ-003-002) ─────────────────────────────────── */}
      {/* SCOPE — 5 of section 3's 6 entries are covered here; "Le mie opportunità" is NOT, and
          that is deliberate rather than an oversight. No Opportunity entity exists anywhere in
          platform/Backend (verified by search: no type, table or endpoint), and the spec treats an
          Opportunity as a distinct stage between Lead and Contract rather than a filter over
          either. Linking this entry to /contracts or /leads would therefore be a mislabelled
          shortcut, not the capability. It stays open until the Opportunity entity exists —
          REQ-005-001's own row records the same missing entity from the funnel side. */}
      {/* Spec section 3: an agent must reach New Practice/My Practices/My Tasks/My Production/My
          Commissions immediately. New Practice is now a sidebar entry (see App.tsx) and My
          Practices/My Tasks/My Commissions are just /contracts, /operations?tab=tasks and
          /commissions themselves — ADR-008 row-level visibility (ResolveVisibleAgentIdsAsync,
          replicated in ContractsController/TasksController/AgentCommissionsController) already
          narrows each of those to exactly this caller's own rows for a restricted-tier session, so
          no new "mine" filter or endpoint is added here, only the labelled entry point. The
          contratti/fatturato figures below are the SAME period- and ADR-008-filtered numbers Row 1
          above already shows — reused, not refetched — for the identical reason: they are already
          this caller's own production for a restricted-tier session.
          Gated on the three external, visibility-restricted roles (`EqualAgente`/`EqualAgenzia`/
          `EqualSegnalatore` — the same trio ContractsController/CustomersController/TasksController
          name in their own ADR-008 comments) rather than shown unconditionally: StatsController
          resolves visibleAgentIds once and returns company-wide totals to any caller ADR-008 does
          NOT restrict (Admin, or a UserCategory with CanSeeAllContracts), so an unconditional "La
          mia produzione" label would misrepresent a privileged caller's own company-wide figures as
          personal production — the same literal-role-list style Dashboard's Admin-only row above
          already uses, just for the opposite (restricted-tier) end of the spectrum. */}
      {(auth.role === 'EqualAgente' || auth.role === 'EqualAgenzia' || auth.role === 'EqualSegnalatore') && (
        <Card variant="outlined" padding="none">
          <CardHeader title="La mia produzione" />
          <CardContent>
            <StatGrid>
              <StatCard label="Contratti" value={formatNum(contratti)} />
              <StatCard label="Fatturato" value={formatEurOrDash(fatturato)} />
            </StatGrid>
            <div className="d-flex flex-wrap gap-2 mt-3">
              <ButtonLink to="/contracts" variant="outlined" size="sm">
                Le mie pratiche
              </ButtonLink>
              <ButtonLink to="/operations?tab=tasks" variant="outlined" size="sm">
                I miei task
              </ButtonLink>
              <ButtonLink to="/commissions" variant="outlined" size="sm">
                Le mie provvigioni
              </ButtonLink>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Row 2 – Tables ───────────────────────────────────────────────── */}
      <CardGrid min={360}>
        {/* Prodotti per Tipo */}
        <Card variant="outlined" padding="none">
          <CardHeader title="Prodotti per tipo" />
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Tipo</th>
                <th className="text-end">N.</th>
                <th className="text-end">Importo</th>
              </tr>
            </thead>
            <tbody>
              {stats && stats.productsByType.length > 0 ? (
                stats.productsByType.map(row => (
                  <tr key={row.category}>
                    <td>{row.category}</td>
                    <td className="text-end md-num">{formatNum(row.count)}</td>
                    {/* TASK-0200: em-dash when `value` is absent (caller without
                          CanSeeRealAmounts) — the row itself, its category and its count stay. */}
                    <td className="text-end md-num">{formatEurOrDash(row.value)}</td>
                  </tr>
                ))
              ) : (
                <EmptyRow cols={3} />
              )}
            </tbody>
          </DataTable>
        </Card>

        {/* Per Operatore */}
        <Card variant="outlined" padding="none">
          <CardHeader title="Per operatore" />
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Operatore</th>
                <th className="text-end">N.</th>
                <th className="text-end">Importo</th>
                <th className="text-end">Conf.</th>
              </tr>
            </thead>
            <tbody>
              {stats && stats.contractsByOperator.length > 0 ? (
                stats.contractsByOperator.map(row => (
                  <tr key={row.operatorName}>
                    <td>{row.operatorName}</td>
                    <td className="text-end md-num">{formatNum(row.count)}</td>
                    {/* TASK-0200: em-dash when `value` is absent — operatorName/count/
                          confirmedCount are never gated. */}
                    <td className="text-end md-num">{formatEurOrDash(row.value)}</td>
                    <td className="text-end md-num">{formatNum(row.confirmedCount)}</td>
                  </tr>
                ))
              ) : (
                <EmptyRow cols={4} />
              )}
            </tbody>
          </DataTable>
        </Card>

        {/* IBAN & Stato Incasso */}
        <Card variant="outlined" padding="none">
          <CardHeader title="IBAN &amp; stato incasso" />
          <CardContent>
            {/* Con / Senza IBAN split */}
            <div className="d-flex gap-3 mb-3">
              <div className="text-center flex-fill">
                <div className="md-typescale-title-large md-num">0</div>
                <div className="text-muted md-typescale-body-small">Con IBAN</div>
              </div>
              <div className="vr" />
              <div className="text-center flex-fill">
                <div className="md-typescale-title-large md-num">0</div>
                <div className="text-muted md-typescale-body-small">Senza IBAN</div>
              </div>
            </div>

            {/* Collection distribution */}
            <EmptyState text="Nessun dato" />
          </CardContent>
        </Card>
      </CardGrid>

      {/* ── Row 3 – Admin stats (shown only for admin role) ──────────────── */}
      {/* Pre-existing bug (predates this session, commit 30eb446): compared against the literal
          lowercase 'admin', which never matches this system's real role values (Administrator,
          EqualAdmin, ...) — so this whole row, including TASK-0030's newly-wired Stato
          Contratti/Gare Attive widgets, has never rendered for any real user. Fixed to check the
          actual Admin-tier role values (same two roles PolicyNames.Admin grants server-side). */}
      <OperatorMonthlySection />
      {(auth.role === 'Administrator' || auth.role === 'EqualAdmin') && <AnnualRevenueSection />}
      {(auth.role === 'Administrator' || auth.role === 'EqualAdmin') && (
        <CardGrid min={260}>
          {/* Valore per Utente */}
          <CardGridItem span={2}>
            <Card variant="outlined" padding="none" className="h-100">
              <CardHeader title="Valore per utente" />
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Utente</th>
                    <th className="text-end">N.</th>
                    <th className="text-end">Fatturato</th>
                    <th className="text-end">Confermato</th>
                  </tr>
                </thead>
                <tbody>
                  <EmptyRow cols={4} />
                </tbody>
              </DataTable>
            </Card>
          </CardGridItem>

          {/* Stato Contratti */}
          <Card variant="outlined" padding="none">
            <CardHeader title="Stato contratti" />
            <CardContent>
              {stats && stats.contractsByStatus.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {stats.contractsByStatus.map(row => (
                    <li key={row.status} className="d-flex justify-content-between border-bottom py-1">
                      <span className="text-muted md-typescale-body-small">{row.status}</span>
                      <span className="md-num md-typescale-emphasized-body-medium">{formatNum(row.count)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState text="Nessun dato" />
              )}
            </CardContent>
          </Card>

          {/* Lead Persi per Motivo — TASK-0191 / REQ-069-002. Modeled directly on Stato
              Contratti above: same Card/CardHeader/CardContent structure, same EmptyState
              fallback, same list-unstyled/li rendering. Unlike Stato Contratti, the underlying
              data is NOT ADR-008-visibility-filtered (see StatsController.Get's class remarks —
              Lead data, not Contract data, company-wide for every authenticated user). */}
          <Card variant="outlined" padding="none">
            <CardHeader title="Lead persi per motivo" />
            <CardContent>
              {stats && stats.leadsByLostReason.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {stats.leadsByLostReason.map(row => (
                    <li key={row.reason} className="d-flex justify-content-between border-bottom py-1">
                      <span className="text-muted md-typescale-body-small">{row.reason}</span>
                      <span className="md-num md-typescale-emphasized-body-medium">{formatNum(row.count)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState text="Nessun dato" />
              )}
            </CardContent>
          </Card>

          {/* Gare Attive */}
          <Card variant="outlined" padding="none">
            <CardHeader title="Gare attive" />
            <CardContent>
              {stats && stats.activeContests.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {stats.activeContests.map(contest => (
                    <li key={contest.id} className="border-bottom py-1">
                      <div className="md-typescale-emphasized-body-medium">{contest.name}</div>
                      <div className="text-muted md-typescale-body-small">
                        {formatDate(contest.startDate)} — {formatDate(contest.endDate)}
                        {contest.prize ? ` · ${contest.prize}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState text="Nessuna gara attiva" />
              )}
            </CardContent>
          </Card>
        </CardGrid>
      )}

      {/* ── Obiettivi Attivi ──────────────────────────────────────────────── */}
      <Card variant="outlined" padding="none">
        <CardHeader title="Obiettivi attivi" />
        {stats && stats.activeTargets.length > 0 ? (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th>
                <th className="text-end">Target</th>
                <th>Stato</th>
                <th>Dal</th>
                <th>Al</th>
              </tr>
            </thead>
            <tbody>
              {stats.activeTargets.map(target => (
                <tr key={target.id}>
                  <td>{target.name}</td>
                  <td className="text-end md-num">{formatEur(target.targetValue)}</td>
                  <td>{target.status}</td>
                  <td>{formatDate(target.startDate)}</td>
                  <td>{formatDate(target.endDate)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <CardContent>
            <EmptyState text="Nessun obiettivo attivo" />
          </CardContent>
        )}
      </Card>

      {/* ── Row 4 – Recent contracts + Upcoming appointments ─────────────── */}
      <CardGrid min={360}>
        {/* Contratti Recenti */}
        <Card variant="outlined" padding="none">
          <CardHeader title="Contratti recenti" />
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Rif.</th>
                <th>Cliente</th>
                <th>Operatore</th>
                <th className="text-end">Importo</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {stats && stats.recentContracts.length > 0 ? (
                stats.recentContracts.map(contract => (
                  <tr key={contract.id}>
                    <td>{contract.referenceCode}</td>
                    <td>{contract.customerName ?? '—'}</td>
                    <td>{contract.operatorName}</td>
                    {/* TASK-0200: em-dash when `amount` is absent — the contract row, its
                          reference, customer, operator and status all stay visible. */}
                    <td className="text-end md-num">{formatEurOrDash(contract.amount)}</td>
                    <td>{contract.status}</td>
                  </tr>
                ))
              ) : (
                <EmptyRow cols={5} />
              )}
            </tbody>
          </DataTable>
        </Card>

        {/* Prossimi Appuntamenti */}
        <Card variant="outlined" padding="none">
          <CardHeader title="Prossimi appuntamenti" />
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Data</th>
                <th>Titolo</th>
                <th>Contatto</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {stats && stats.upcomingAppointments.length > 0 ? (
                stats.upcomingAppointments.map(appt => (
                  <tr key={appt.id}>
                    <td>{formatDateTime(appt.startAt)}</td>
                    <td>{appt.title}</td>
                    <td>{appt.contactName ?? '—'}</td>
                    <td>{appt.status}</td>
                  </tr>
                ))
              ) : (
                <EmptyRow cols={4} />
              )}
            </tbody>
          </DataTable>
        </Card>
      </CardGrid>
    </Page>
  )
}
